import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { HiArrowLeft, HiSearch, HiX, HiAdjustments } from "react-icons/hi";

import { stonesData } from "../data/stonesData";
import { trackEvent, getProducts } from "../backend/utils/orderUtils";
import { getEmojiForPlanet, mapDbProduct } from "../utils/productMap";
import { PRODUCT_CATEGORIES, categoryLabel } from "../utils/categories";
/* Aliased: `trackEvent` above is the shop's own Supabase logging, these
   are Google Analytics. Both are wanted — one feeds the admin dashboard,
   the other the GA reports. */
import {
  trackAddToCart as gaAddToCart,
  trackFilter as gaFilter,
  trackSearch as gaSearch,
  trackSelectItem as gaSelectItem,
  trackViewItem as gaViewItem,
  trackViewItemList as gaViewItemList,
} from "../utils/analytics";
import { useCart } from "../context/CartContext";
import ImageLightbox from "./ImageLightbox";
import ProductModal from "./ProductModal";
import ProductCard from "./ProductCard";
import useScrollLock from "../utils/useScrollLock";

import "../styles/Shop.css";

/* Everything a customer might reasonably type, flattened into one
   string per product. Category is matched on its LABEL, so searching
   "loose stones" finds products stored as "loose-stones". */
const haystack = (product) =>
  [
    product.name,
    product.planet,
    categoryLabel(product.category),
    product.description,
    ...(Array.isArray(product.tags) ? product.tags : []),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

/* Every word has to appear somewhere, in any order — so "venus
   bracelet" finds a Morganite Bracelet ruled by Venus, which a
   straight phrase match would miss. */
const matchesSearch = (product, terms) => {
  if (terms.length === 0) return true;
  const text = haystack(product);
  return terms.every((term) => text.includes(term));
};

const Shop = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const planetParam = searchParams.get("planet");

  // Seeded from the URL so a shared /shop?q=ruby link opens on results.
  const [query, setQuery] = useState(() => searchParams.get("q") || "");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedPlanet, setSelectedPlanet] = useState(planetParam || "all");
  const [products, setProducts] = useState(stonesData); // Fallback to local data
  const [loading, setLoading] = useState(true);
  const [zoomProduct, setZoomProduct] = useState(null); // product shown in lightbox
  const [detailProduct, setDetailProduct] = useState(null); // full-info modal
  const [justAdded, setJustAdded] = useState(null); // { id, at } — button feedback
  /* Phones get one "Filters" button instead of two dropdowns; this is
     the sheet it opens. Desktop still uses the chip row. */
  const [sheetOpen, setSheetOpen] = useState(false);

  useScrollLock(sheetOpen);
  const navigate = useNavigate();
  const { addItem } = useCart();

  const loadProducts = useCallback(async () => {
    setLoading(true);

    const { success, products: dbProducts } = await getProducts();

    if (success && dbProducts.length > 0) {
      setProducts(dbProducts.map(mapDbProduct));
    } else {
      // Fallback to local data if Supabase fails
      console.warn('Using local product data');
      setProducts(stonesData);
    }
    
    setLoading(false);
  }, []);

  // Load products from Supabase on mount
  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const terms = useMemo(
    () => query.trim().toLowerCase().split(/\s+/).filter(Boolean),
    [query]
  );

  /* The filters are applied in stages so each control can be counted
     against everything EXCEPT itself. A category chip reading "(3)"
     then means three pieces once you tap it — counting the whole
     catalogue instead would promise stock the current search has
     already ruled out. */
  const searched = useMemo(
    () => products.filter((p) => matchesSearch(p, terms)),
    [products, terms]
  );

  const beforeCategory = useMemo(
    () =>
      selectedPlanet === "all"
        ? searched
        : searched.filter((p) => p.planet === selectedPlanet),
    [searched, selectedPlanet]
  );

  const categories = useMemo(() => {
    const counts = beforeCategory.reduce((acc, p) => {
      if (p.category) acc[p.category] = (acc[p.category] || 0) + 1;
      return acc;
    }, {});

    // Every category is listed whether or not it has stock, so customers
    // can see the full range on offer. A category with nothing in it
    // shows 0 and falls through to the empty state below.
    return [
      { id: "all", label: "All Products", count: beforeCategory.length },
      ...PRODUCT_CATEGORIES.map((c) => ({
        id: c.value,
        label: c.label,
        count: counts[c.value] || 0,
      })),
    ];
  }, [beforeCategory]);

  // Keep the URL in sync so a filtered shop view is shareable, and so
  // arriving from a planet modal lands on the right filter.
  useEffect(() => {
    if (planetParam && planetParam !== selectedPlanet) {
      setSelectedPlanet(planetParam);
    }
  }, [planetParam, selectedPlanet]);

  const changePlanet = (value) => {
    setSelectedPlanet(value);
    gaFilter('planet', value);

    const next = new URLSearchParams(searchParams);
    if (value === "all") next.delete("planet");
    else next.set("planet", value);
    setSearchParams(next, { replace: true });
  };

  const planets = useMemo(() => {
    const found = new Set(products.map((p) => p.planet).filter(Boolean));
    // Keep the requested planet selectable even if nothing matches yet.
    if (selectedPlanet !== "all") found.add(selectedPlanet);
    return ["all", ...found];
  }, [products, selectedPlanet]);

  const filteredProducts = useMemo(
    () =>
      selectedCategory === "all"
        ? beforeCategory
        : beforeCategory.filter((item) => item.category === selectedCategory),
    [beforeCategory, selectedCategory]
  );

  const changeCategory = (value) => {
    setSelectedCategory(value);
    gaFilter('category', value);
  };

  /* Put the search in the URL so a result set can be shared, and so a
     page view in Google Analytics carries the term people typed.
     Debounced: one entry per search, not one per keystroke.

     The early return matters — setSearchParams is rebuilt whenever the
     query string changes, so without it this effect would re-run and
     navigate a second time to the address it had just reached. */
  const term = query.trim();

  useEffect(() => {
    if (term === (searchParams.get("q") || "")) return;

    const timer = setTimeout(() => {
      const next = new URLSearchParams(searchParams);
      if (term) next.set("q", term);
      else next.delete("q");
      setSearchParams(next, { replace: true });
    }, 300);

    return () => clearTimeout(timer);
  }, [term, searchParams, setSearchParams]);

  /* Reported once the typing settles, with what the customer actually
     got back — a term that returns nothing is the useful signal here. */
  useEffect(() => {
    const term = query.trim();
    if (!term) return;

    const timer = setTimeout(() => gaSearch(term, filteredProducts.length), 800);
    return () => clearTimeout(timer);
    // filteredProducts is deliberately not a dependency: it changes as
    // the grid re-filters and would restart the timer on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const hasFilters =
    query.trim() !== "" || selectedCategory !== "all" || selectedPlanet !== "all";

  // The search box is its own visible control, so it isn't counted here.
  const activeFilterCount =
    (selectedCategory !== "all" ? 1 : 0) + (selectedPlanet !== "all" ? 1 : 0);

  const clearAll = () => {
    setQuery("");
    setSelectedCategory("all");
    changePlanet("all");
  };

  /* Which stones were actually put in front of the customer, and under
     which filter. Fires again whenever the filters change the grid. */
  useEffect(() => {
    if (loading || filteredProducts.length === 0) return;
    gaViewItemList('Shop', filteredProducts);
  }, [loading, filteredProducts]);

  const openDetails = (product) => {
    setDetailProduct(product);
    gaSelectItem(product, 'Shop');
    gaViewItem(product);
  };

  /* There is no "Buy Now" any more. It opened checkout directly from a
     card, which skipped the cart — and with it the discount-code field
     and any chance to order more than one thing. Everything goes
     through the bag now. */
  const handleAddToCart = (product) => {
    addItem(product, 1);
    // New object each time so adding the same stone twice restarts the timer.
    setJustAdded({ id: product.id, at: Date.now() });
    trackEvent({
      eventType: 'add_to_cart',
      productId: product.supabaseId,
    }).catch(() => {});
    gaAddToCart(product, 1, 'Shop');
  };

  // Reset the "Added ✓" label a moment after the last add.
  useEffect(() => {
    if (!justAdded) return;
    const timer = setTimeout(() => setJustAdded(null), 1800);
    return () => clearTimeout(timer);
  }, [justAdded]);

  return (
    <section className="shop-page">
      <div className="shop-header">
        <button 
          className="back-to-home-btn"
          onClick={() => navigate('/')}
        >
          <HiArrowLeft />
          <span>Back to Home</span>
        </button>

        <h1>Our Collection</h1>
        <p>
          Browse our curated range of lab-certified gemstones aligned with
          planetary energies.
        </p>
      </div>

      {/* Filters */}
      <div className="shop-filters">
        <div className="shop-search">
          <HiSearch className="shop-search-icon" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by stone, planet or type…"
            aria-label="Search products"
            autoComplete="off"
          />
          {query && (
            <button
              type="button"
              className="shop-search-clear"
              onClick={() => setQuery("")}
              aria-label="Clear search"
            >
              {/* The button itself is the 44px touch target and stays
                  invisible; this is the small circle you actually see.
                  Sizing the button to the circle instead let the global
                  min-height rule stretch it into an oval. */}
              <span>
                <HiX />
              </span>
            </button>
          )}
        </div>

        {/* Categories read as chips where there's room to lay them all
            out at once. Below 768px they become the dropdown further
            down instead — eleven chips on a phone were either five
            stacked rows or a strip you had to swipe blind. */}
        <div
          className="category-filter"
          role="group"
          aria-label="Filter by category"
        >
          {categories.map((cat) => (
            <button
              key={cat.id}
              className={`filter-btn ${
                selectedCategory === cat.id ? "active" : ""
              } ${cat.count === 0 ? "is-empty" : ""}`}
              onClick={() => changeCategory(cat.id)}
            >
              {cat.label}
              {/* "All Products" is the reset, not a section to weigh up
                  against the others — its total is already in the count
                  line below whenever a filter is on. */}
              {cat.id !== "all" && (
                <span className="filter-count">{cat.count}</span>
              )}
            </button>
          ))}
        </div>

        {/* Phone: one control instead of two dropdowns. The count on
            the right is the number of pieces the current filters
            actually return, so it is worth reading before tapping. */}
        <button
          type="button"
          className="shop-filter-trigger"
          onClick={() => setSheetOpen(true)}
        >
          <HiAdjustments />
          <span>
            Filters
            {activeFilterCount > 0 && ` · ${activeFilterCount} applied`}
          </span>
          <span className="shop-filter-tally">{filteredProducts.length}</span>
        </button>

        <div className="shop-selects">
          <label className="shop-select shop-select-category">
            <span className="shop-select-label">Category</span>
            <select
              value={selectedCategory}
              onChange={(e) => changeCategory(e.target.value)}
            >
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.id === "all" ? cat.label : `${cat.label} (${cat.count})`}
                </option>
              ))}
            </select>
          </label>

          <label className="shop-select">
            <span className="shop-select-label">Planet</span>
            <select
              value={selectedPlanet}
              onChange={(e) => changePlanet(e.target.value)}
            >
              {planets.map((planet) => (
                <option key={planet} value={planet}>
                  {planet === "all" ? "All Planets" : planet}
                </option>
              ))}
            </select>
          </label>
        </div>

        {/* What's applied right now, each removable on its own. */}
        {hasFilters && (
          <div className="shop-active">
            <span className="shop-count">
              {filteredProducts.length}
              {filteredProducts.length === products.length
                ? " pieces"
                : ` of ${products.length}`}
            </span>

            {query.trim() && (
              <button className="shop-chip" onClick={() => setQuery("")}>
                “{query.trim()}” <HiX />
              </button>
            )}

            {selectedCategory !== "all" && (
              <button
                className="shop-chip"
                onClick={() => changeCategory("all")}
              >
                {categoryLabel(selectedCategory)} <HiX />
              </button>
            )}

            {selectedPlanet !== "all" && (
              <button className="shop-chip" onClick={() => changePlanet("all")}>
                {getEmojiForPlanet(selectedPlanet)} {selectedPlanet} <HiX />
              </button>
            )}

            <button className="shop-clear-all" onClick={clearAll}>
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* Products Grid */}
      <div className="shop-grid">
        {loading ? (
          <p className="loading-text">Loading products...</p>
        ) : filteredProducts.length === 0 ? (
          <div className="no-products">
            {/* Name what they actually did — an empty category and a
                search that missed need different answers. */}
            {query.trim() ? (
              <>
                <p>
                  Nothing matches “<strong>{query.trim()}</strong>”
                  {selectedPlanet !== "all" && ` in ${selectedPlanet} stones`}.
                </p>
                <span>
                  Try a stone name, a planet, or a type like “bracelet”.
                </span>
              </>
            ) : selectedCategory !== "all" && selectedPlanet === "all" ? (
              <p>
                No {categoryLabel(selectedCategory)} in stock right now — new
                pieces are added regularly.
              </p>
            ) : (
              <p>No products found for the selected filters.</p>
            )}

            <button className="shop-reset-btn" onClick={clearAll}>
              Show everything
            </button>
          </div>
        ) : (
          filteredProducts.map((product) => (
            <ProductCard
              key={product.id}
              product={product}
              justAdded={justAdded}
              onZoom={setZoomProduct}
              onOpenDetails={openDetails}
              onAddToCart={handleAddToCart}
            />
          ))
        )}
      </div>

      {/* FILTER SHEET — phones only; the chip row covers desktop.
          Choices apply as they're tapped, so the tally on the button
          below is always the live answer rather than a promise. */}
      {sheetOpen && (
        <div
          className="filter-sheet-overlay"
          onClick={() => setSheetOpen(false)}
        >
          <div
            className="filter-sheet"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Filter products"
          >
            <header className="filter-sheet-head">
              <h2>Filters</h2>
              <button
                type="button"
                onClick={() => setSheetOpen(false)}
                aria-label="Close filters"
              >
                <HiX />
              </button>
            </header>

            <div className="filter-sheet-body">
              <h3>Category</h3>
              <div className="filter-list">
                {categories.map((cat) => (
                  <label
                    key={cat.id}
                    className={`filter-row ${
                      selectedCategory === cat.id ? "is-on" : ""
                    } ${cat.count === 0 ? "is-empty" : ""}`}
                  >
                    <input
                      type="radio"
                      name="sheet-category"
                      checked={selectedCategory === cat.id}
                      onChange={() => changeCategory(cat.id)}
                    />
                    <span className="filter-row-label">{cat.label}</span>
                    <span className="filter-row-count">{cat.count}</span>
                  </label>
                ))}
              </div>

              <h3>Planet</h3>
              <div className="filter-planets">
                {planets.map((planet) => (
                  <button
                    key={planet}
                    type="button"
                    className={`filter-planet ${
                      selectedPlanet === planet ? "is-on" : ""
                    }`}
                    onClick={() => changePlanet(planet)}
                  >
                    {planet === "all"
                      ? "All Planets"
                      : `${getEmojiForPlanet(planet)} ${planet}`}
                  </button>
                ))}
              </div>
            </div>

            <footer className="filter-sheet-foot">
              <button
                type="button"
                className="filter-clear"
                onClick={clearAll}
                disabled={!hasFilters}
              >
                Clear all
              </button>
              <button
                type="button"
                className="filter-apply"
                onClick={() => setSheetOpen(false)}
              >
                Show {filteredProducts.length}{" "}
                {filteredProducts.length === 1 ? "piece" : "pieces"}
              </button>
            </footer>
          </div>
        </div>
      )}

      {/* FULL PRODUCT DETAILS */}
      {detailProduct && (
        <ProductModal
          product={detailProduct}
          onAddToCart={handleAddToCart}
          onClose={() => setDetailProduct(null)}
        />
      )}

      {/* PRODUCT IMAGE LIGHTBOX */}
      {zoomProduct && (
        <ImageLightbox
          src={zoomProduct.image}
          alt={zoomProduct.name}
          onClose={() => setZoomProduct(null)}
        />
      )}
    </section>
  );
};

export default Shop;